// frontend/src/state/analysis/analysisThunks.js
// Purpose: Define asynchronous thunks for API interactions in the analysis workflow
// Desired outcome: Fix PDF and Excel export functionality by correctly forming API request URLs and handling downloads properly

import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';
// Import the actions from the slice file, not from the reducers file directly
import { setCalculations, setCalculationsLoading, setCalculationsError, setReportGenerating, setReportError, updateReportDownloadURLs, setReportGeneratedAt, setStep, resetValueSavings, setCalculationsScenarioC, setCalculationsLoadingScenarioC, setCalculationsErrorScenarioC } from './analysisSlice';

// Fetch a list of uploaded files from the server
const fetchUploadedFiles = createAsyncThunk(
  'analysis/fetchUploadedFiles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/upload');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Upload a file to the server
const uploadFile = createAsyncThunk(
  'analysis/uploadFile',
  async (fileData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', fileData);
      const response = await api.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.file;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Delete a file by file ID
const deleteFile = createAsyncThunk(
  'analysis/deleteFile',
  async (fileID, { rejectWithValue }) => {
    try {
      await api.delete(`/api/upload/${fileID}`);
      return fileID;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Select a file for analysis
const selectFile = createAsyncThunk(
  'analysis/selectFile',
  async (fileID, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/files/select', { fileID });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch column headers for a file
const fetchFileColumns = createAsyncThunk(
  'analysis/fetchFileColumns',
  async (fileID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/files/${fileID}/columns`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch field mappings from the server
const fetchFieldMappings = createAsyncThunk(
  'analysis/fetchFieldMappings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/mappings/fields');
      return response.data.fieldMappings;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch suggested column mappings
const fetchSuggestedMappings = createAsyncThunk(
  'analysis/fetchSuggestedMappings',
  async (fileID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/mappings/suggested/${fileID}`);
      return response.data.suggestedMappings;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Confirm column mappings
const confirmColumnMappings = createAsyncThunk(
  'analysis/confirmColumnMappings',
  async ({ fileID, confirmedMappings }, { rejectWithValue }) => {
    try {
      await api.post('/api/mappings/confirm', { fileID, confirmedMappings });
      return confirmedMappings;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch confirmed mappings
const fetchConfirmedMappings = createAsyncThunk(
  'analysis/fetchConfirmedMappings',
  async (fileID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/mappings/${fileID}`);
      return response.data.confirmedMappings;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch all existing reports
const fetchReports = createAsyncThunk(
  'analysis/fetchReports',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/reports');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch a single report by ID
const fetchReportById = createAsyncThunk(
  'analysis/fetchReportById',
  async (reportID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/reports/${reportID}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Generate a new report
const generateReport = createAsyncThunk(
  'analysis/generateReport',
  async ({ fileID, name, config }, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setReportGenerating(true));
      console.log('Starting report generation process...');
      
      // Create the report payload
      const reportPayload = {
        fileID, 
        name, 
        config 
      };
      
      console.log('Sending report generation request with payload:', reportPayload);
      
      // Make the API request to generate the report
      const response = await api.post('/api/reports', reportPayload);
      
      if (!response.data) {
        throw new Error('Invalid response from server - no data returned');
      }
      
      console.log('Report generation response:', response.data);
      
      // Generate download URLs
      const baseUrl = window.location.origin;
      const downloadURLs = {
        pdf: `${baseUrl}/api/reports/export/pdf?reportID=${response.data.reportID}`,
        excel: `${baseUrl}/api/reports/export/excel?reportID=${response.data.reportID}`
      };
      
      // Update the download URLs in the report data
      dispatch(updateReportDownloadURLs(downloadURLs));
      
      dispatch(setReportGenerating(false));
      // Set the report generation timestamp
      dispatch(setReportGeneratedAt(new Date().toISOString()));
      
      return {
        ...response.data,
        downloadURLs
      };
    } catch (error) {
      console.error('Error generating report:', error);
      dispatch(setReportGenerating(false));
      dispatch(setReportError(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Delete a report by ID
const deleteReport = createAsyncThunk(
  'analysis/deleteReport',
  async (reportID, { rejectWithValue }) => {
    try {
      await api.delete(`/api/reports/${reportID}`);
      return reportID;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Bootstrap process to initialize dependent data in sequence
const bootstrapAnalysisData = createAsyncThunk(
  'analysis/bootstrapAnalysisData',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState().analysis;

      // If bootstrap has already completed, return early
      if (state.bootstrapComplete) {
        console.log("✅ Bootstrap already completed, skipping.");
        return { alreadyCompleted: true };
      }

      dispatch({ type: 'analysis/bootstrapStarted' });

      console.log("🔄 Starting bootstrap process...");

      // Step 1: Fetch categories first
      const categories = await dispatch(fetchAllCategories()).unwrap();

      // Ensure categories are available before proceeding
      if (!categories || categories.length === 0) {
        throw new Error("No categories available.");
      }

      // Step 2: Fetch part matches (only after categories are available)
      if (!state.currentFile || !state.currentFile.fileID) {
        console.warn("🚨 No valid fileID found. Skipping part match fetching.");
        return rejectWithValue("No valid file selected.");
      }

      console.log(`🔄 Fetching part matches for fileID: ${state.currentFile.fileID}`);
      const partMatches = await dispatch(fetchSuggestedPartMatches({
        fileID: state.currentFile.fileID,
        threshold: 70
      })).unwrap();

      console.log("✅ Bootstrap completed successfully!");
      dispatch({ type: 'analysis/bootstrapCompleted', payload: { categories, partMatches } });

      return { categories, partMatches };
    } catch (error) {
      console.error("❌ Bootstrap process failed:", error.message);
      dispatch({ type: 'analysis/bootstrapFailed', payload: error.message });
      return rejectWithValue(error.message);
    }
  }
);

// Fetch all available categories
const fetchAllCategories = createAsyncThunk(
  'analysis/fetchAllCategories',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { availableCategories } = getState().analysis;

      // Skip fetching if we already have categories loaded
      if (availableCategories && availableCategories.length > 0) {
        console.log("✅ Categories already fetched, skipping API call.");
        return availableCategories;
      }

      console.log("🔄 Fetching categories from API...");
      const response = await api.get('/api/categories');

      if (!response.data?.categories) {
        throw new Error("Invalid response: categories missing");
      }

      console.log(`✅ Successfully fetched ${response.data.categories.length} categories`);
      return response.data.categories;
    } catch (error) {
      console.error("❌ Error fetching categories:", error.message);
      return rejectWithValue(error.message || "Failed to load categories");
    }
  }
);

// Fetch all OEM parts
const fetchAllOemParts = createAsyncThunk(
  'analysis/fetchAllOemParts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { oemParts } = getState().analysis;

      // Skip fetching if we already have OEM parts loaded
      if (oemParts && oemParts.length > 0) {
        console.log("✅ OEM parts already fetched, skipping API call.");
        return oemParts;
      }

      console.log("🔄 Fetching OEM parts from API...");
      const response = await api.get('/api/categories/oem');

      if (!response.data?.oemParts) {
        throw new Error("Invalid response: oemParts missing");
      }

      console.log(`✅ Successfully fetched ${response.data.oemParts.length} OEM parts`);
      return response.data.oemParts;
    } catch (error) {
      console.error("❌ Error fetching OEM parts:", error.message);
      return rejectWithValue(error.message || "Failed to load OEM parts");
    }
  }
);

// Fetch suggested part matches - FIXED URL FORMAT
const fetchSuggestedPartMatches = createAsyncThunk(
  'analysis/fetchSuggestedPartMatches',
  async ({ fileID, threshold = 70 }, { getState, rejectWithValue }) => {
    try {
      console.log(`🔄 Fetching part matches for fileID: ${fileID} with threshold: ${threshold}`);
      // Fix: Properly format the URL with template literals
      const response = await api.get(`/api/matching/suggested/${fileID}?threshold=${threshold}`);

      if (!response.data?.partMatches) {
        throw new Error("Invalid response: partMatches missing");
      }

      console.log(`✅ Successfully fetched ${response.data.partMatches.length} part matches`);
      return response.data.partMatches;
    } catch (error) {
      console.error(`❌ Error fetching part matches for fileID ${fileID}:`, error.message);
      return rejectWithValue(error.message || "Failed to fetch part matches");
    }
  }
);

// Set confidence threshold for part matching
const setConfidenceThreshold = createAsyncThunk(
  'analysis/setConfidenceThreshold',
  async ({ fileID, confidenceThreshold }, { rejectWithValue }) => {
    try {
      console.log(`🔄 Setting confidence threshold to ${confidenceThreshold} for fileID: ${fileID}`);
      await api.post('/api/matching/setThreshold', { fileID, confidenceThreshold });
      return confidenceThreshold;
    } catch (error) {
      console.error("❌ Error setting confidence threshold:", error.message);
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Store manual matches for parts
const storeManualMatches = createAsyncThunk(
  'analysis/storeManualMatches',
  async ({ fileID, manualMatches }, { rejectWithValue }) => {
    try {
      await api.post('/api/matching/manual', { fileID, manualMatches });
      return manualMatches;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch confirmed part matches
const fetchConfirmedPartMatches = createAsyncThunk(
  'analysis/fetchConfirmedPartMatches',
  async (fileID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/matching/confirmed/${fileID}`);
      return response.data.partMatches;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Confirm all part matches
const confirmPartMatches = createAsyncThunk(
  'analysis/confirmPartMatches',
  async ({ fileID, partMatches }, { rejectWithValue }) => {
    try {
      await api.post('/api/matching/confirm', { fileID, partMatches });
      return partMatches;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch contextualized parts
const fetchContextualizedParts = createAsyncThunk(
  'analysis/fetchContextualizedParts',
  async (fileID, { getState, rejectWithValue }) => {
    try {
      console.log(`🔄 Fetching contextualized parts for fileID: ${fileID}`);

      if (!fileID) {
        console.error("❌ No valid fileID provided to fetchContextualizedParts");
        return rejectWithValue("No valid file selected.");
      }

      const response = await api.get(`/api/context/${fileID}`);

      if (!response.data?.contextualizedParts) {
        console.error("❌ Invalid response: contextualizedParts missing");
        return rejectWithValue("Invalid response from server");
      }

      console.log(`✅ Successfully fetched ${response.data.contextualizedParts.length} contextualized parts`);
      return response.data.contextualizedParts;
    } catch (error) {
      console.error(`❌ Error fetching contextualized parts for fileID ${fileID}:`, error.message);
      return rejectWithValue(error.message || "Failed to fetch contextualized Parts");
    }
  }
);

// Confirm contextualized parts
const confirmContextualizedParts = createAsyncThunk(
  'analysis/confirmContextualizedParts',
  async ({ fileID, confirmedContextualizedParts }, { rejectWithValue }) => {
    try {
      await api.post('/api/context/confirm', { fileID, confirmedContextualizedParts });
      return confirmedContextualizedParts;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// ✅ Fetch calculations for the selected file
const fetchCalculations = createAsyncThunk(
  'analysis/fetchCalculations',
  async ({ fileID, scenario }, { dispatch, rejectWithValue }) => {
    try {
      console.log(`🔄 Fetching calculations for fileID: ${fileID}, Scenario: ${scenario || 'default'}`);

      if (!fileID) {
        console.error("❌ No valid fileID provided to fetchCalculations");
        return rejectWithValue("No valid file selected.");
      }

      dispatch(setCalculationsLoading()); // ✅ Set loading state before API call

      const response = await api.post('/api/calculations', { fileID, scenario });

      if (!response.data?.calculations) {
        console.error("❌ Invalid response: calculations missing");
        return rejectWithValue("Invalid response from server");
      }

      console.log(`✅ Successfully fetched calculations for fileID: ${fileID}`);
      dispatch(setCalculations({ data: response.data.calculations, metadata: response.data.metadata }));

      return response.data.calculations;
    } catch (error) {
      console.error(`❌ Error fetching calculations for fileID ${fileID}:`, error.message);
      dispatch(setCalculationsError(error.response?.data?.message || "Failed to fetch calculations"));
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Check if file is ready for calculations
const checkCalculationReadiness = createAsyncThunk(
  'analysis/checkCalculationReadiness',
  async (fileID, { rejectWithValue }) => {
    try {
      // Ensure fileID is provided and valid
      if (!fileID) {
        throw new Error('Missing fileID parameter');
      }

      // Use the correct URL format
      const response = await api.get(`/api/calculations/check/${fileID}`);

      // Validate the response to ensure it contains missingDataCheck field
      if (!response.data?.missingDataCheck) {
        throw new Error("Invalid response: missing data check information not found");
      }

      return response.data.missingDataCheck;
    } catch (error) {
      console.error(`Error checking calculation readiness for fileID ${fileID}:`, error);
      return rejectWithValue(error.response?.data || { message: "Failed to check calculation readiness" });
    }
  }
);

// Run calculations for a file with selected scenarios
const runCalculations = createAsyncThunk(
  'analysis/runCalculations',
  async ({ fileID, selectedScenarios }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post('/api/calculations/run', { fileID, selectedScenarios });
      const calculationResults = response.data.calculationResults;

      // Dispatch results to appropriate state slices for different scenarios
      if (calculationResults) {
        dispatch(setCalculations({ 
          data: calculationResults, 
          metadata: response.data.metadata 
        }));
        
        // Handle scenario_C data specifically
        if (selectedScenarios.includes('scenario_C')) {
          if (calculationResults.summary && calculationResults.summary.scenario_C) {
            dispatch(setCalculationsScenarioC(calculationResults.summary.scenario_C));
          }
        }
      }

      return calculationResults;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Run specific scenario C calculations
const runCalculationsScenarioC = createAsyncThunk(
  'analysis/runCalculationsScenarioC',
  async ({ fileID }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setCalculationsLoadingScenarioC());
      const response = await api.post('/api/calculations/run', { 
        fileID, 
        selectedScenarios: ['scenario_C'] 
      });
      
      if (response.data.calculationResults && response.data.calculationResults.summary && 
          response.data.calculationResults.summary.scenario_C) {
        dispatch(setCalculationsScenarioC(response.data.calculationResults.summary.scenario_C));
        return response.data.calculationResults.summary.scenario_C;
      } else {
        throw new Error('No scenario_C results found in response');
      }
    } catch (error) {
      dispatch(setCalculationsErrorScenarioC(error.response?.data || { message: error.message }));
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Confirm calculations
const confirmCalculations = createAsyncThunk(
  'analysis/confirmCalculations',
  async ({ fileID, calculationStatus }, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/api/calculations/confirm', { fileID, calculationStatus });
      // Reset assumptions and initialize state for Value Savings Analysis
      dispatch(resetValueSavings());
      // Set step to Value Savings Analysis explicitly
      dispatch(setStep('valueSavingsAnalysis'));
      return calculationStatus;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Get calculation results
const getCalculationResults = createAsyncThunk(
  'analysis/getCalculationResults',
  async (fileID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/calculations/${fileID}`);
      
      // Return the results if they exist
      if (response.data.success && response.data.calculationResults) {
        return response.data.calculationResults;
      } else {
        // Return empty structure if no calculations exist yet
        return {
          parts:[],
          summary: {}
        };
      }
    } catch (error) {
      // Instead of rejecting, return empty structure
      return {
        parts:[],
        summary: {}
      };
    }
  }
);

// Calculate value savings for a file
const calculateValueSavings = createAsyncThunk(
  'analysis/calculateValueSavings',
  async ({ fileID, scenarioType, assumptions }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/valueSavings/calculate', {
        fileID,
        scenarioType,
        assumptions
      });
      
      return response.data.calculationResults;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Get value savings analysis for a file
const getValueSavingsAnalysis = createAsyncThunk(
  'analysis/getValueSavingsAnalysis',
  async (fileID, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/valueSavings/${fileID}`);
      
      return response.data.valueSavingsAnalysis;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Export report as PDF - FIXED IMPLEMENTATION
const exportReportPdf = createAsyncThunk(
  'analysis/exportReportPdf',
  async ({ reportID }, { dispatch, rejectWithValue }) => {
    try {
      console.log(`Attempting to download PDF report for ID: ${reportID}`);
      const result = await api.downloadFile('pdf', reportID);
      if (result.success) {
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/api/reports/export/pdf?reportID=${reportID}`;
        dispatch(updateReportDownloadURLs({ pdf: url }));
        return { success: true, url };
      } else {
        throw new Error(result.error || 'Failed to download PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF report:', error);
      dispatch(setReportError(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Export report as Excel - FIXED IMPLEMENTATION
const exportReportExcel = createAsyncThunk(
  'analysis/exportReportExcel',
  async ({ reportID }, { dispatch, rejectWithValue }) => {
    try {
      console.log(`Attempting to download Excel report for ID: ${reportID}`);
      const result = await api.downloadFile('excel', reportID);
      if (result.success) {
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/api/reports/export/excel?reportID=${reportID}`;
        dispatch(updateReportDownloadURLs({ excel: url }));
        return { success: true, url };
      } else {
        throw new Error(result.error || 'Failed to download Excel file');
      }
    } catch (error) {
      console.error('Error downloading Excel report:', error);
      dispatch(setReportError(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

export {
  fetchCalculations,
  bootstrapAnalysisData,
  fetchUploadedFiles,
  uploadFile,
  deleteFile,
  selectFile,
  fetchFileColumns,
  fetchFieldMappings,
  fetchSuggestedMappings,
  confirmColumnMappings,
  fetchConfirmedMappings,
  fetchAllCategories,
  fetchAllOemParts,
  fetchReports,
  fetchReportById,
  generateReport,
  deleteReport,
  fetchSuggestedPartMatches,
  setConfidenceThreshold,
  storeManualMatches,
  fetchConfirmedPartMatches,
  confirmPartMatches,
  fetchContextualizedParts,
  confirmContextualizedParts,
  checkCalculationReadiness,
  runCalculations,
  runCalculationsScenarioC,
  confirmCalculations,
  getCalculationResults,
  calculateValueSavings,
  getValueSavingsAnalysis,
  exportReportPdf,
  exportReportExcel
};